<script setup lang="ts">
import { ElNotification } from "element-plus"
import { computed, onBeforeUnmount, onMounted, watch } from "vue"
import { useRouter } from "vue-router"
import { enqueueNotificationSpeech } from "@/components/Notify/notificationSpeechQueue"
import { usesCentralReservation } from "@/features/reservation-pending/experience"
import {
  buildReservationPendingRoute,
  createReservationPendingEventDeduper,
  parseReservationStatusChangedMessage,
  RESERVATION_PENDING_REFRESH_EVENT,
  shouldAlertReservationPendingEvent,
} from "@/features/reservation-pending/realtime"
import { useReservationPendingStore } from "@/store/modules/reservationPending"
import { useSettingsStore } from "@/store/modules/settings"
import { useUserStore } from "@/store/modules/user"
import { useViewScopeStore } from "@/store/modules/viewScope"
import { useWsStore } from "@/store/modules/wsStore"
import { resolveActiveShopId } from "@/utils/shopScope"

const ALERT_TEXT = "您有新的网订申请，请及时处理"
const userStore = useUserStore()
const viewScopeStore = useViewScopeStore()
const settingsStore = useSettingsStore()
const wsStore = useWsStore()
const pendingStore = useReservationPendingStore()
const router = useRouter()
const deduper = createReservationPendingEventDeduper(200)
let refreshTimer: number | undefined

const activeShopId = computed(() => resolveActiveShopId({
  activeScope: viewScopeStore.activeScope,
  userShopId: userStore.shopId,
}))
const canView = computed(() => usesCentralReservation(viewScopeStore.activeScope?.type)
  && userStore.checkPermission("business_booking"))

function scheduleAuthoritativeRefresh(delay = 120) {
  if (refreshTimer !== undefined) window.clearTimeout(refreshTimer)
  if (!canView.value) return
  refreshTimer = window.setTimeout(() => {
    refreshTimer = undefined
    if (canView.value) pendingStore.refresh()
  }, delay)
}

function navigateToPendingWorkbench() {
  if (!canView.value) return
  return router.push(buildReservationPendingRoute(pendingStore.earliestPendingDate))
    .catch(() => undefined)
}

function handleRealtimeMessage(raw: unknown) {
  const event = parseReservationStatusChangedMessage(raw)
  if (!event || !canView.value || !activeShopId.value || event.shopId !== activeShopId.value) return

  const eventIsNew = deduper.remember(event.eventId)
  scheduleAuthoritativeRefresh()
  if (!settingsStore.showNotify || !shouldAlertReservationPendingEvent(event, {
    canView: canView.value,
    eventIsNew,
    shopId: activeShopId.value,
  })) return

  const notification = ElNotification({
    title: "网订待确认",
    message: ALERT_TEXT,
    type: "warning",
    duration: 0,
    onClick: () => {
      notification.close()
      navigateToPendingWorkbench()
    },
  })
  enqueueNotificationSpeech(ALERT_TEXT)
}

function handleVisibilityChange() {
  if (document.visibilityState === "visible") scheduleAuthoritativeRefresh(0)
}

function handleLocalRefresh() {
  scheduleAuthoritativeRefresh(0)
}

watch(
  () => [activeShopId.value, canView.value] as const,
  ([shopId, hasAccess]) => pendingStore.activate(shopId, hasAccess),
  { immediate: true },
)
watch(() => wsStore.messageEpoch, () => handleRealtimeMessage(wsStore.data), { flush: "sync" })
watch(() => wsStore.connectionEpoch, () => scheduleAuthoritativeRefresh(0))

onMounted(() => {
  document.addEventListener("visibilitychange", handleVisibilityChange)
  window.addEventListener(RESERVATION_PENDING_REFRESH_EVENT, handleLocalRefresh)
})

onBeforeUnmount(() => {
  if (refreshTimer !== undefined) window.clearTimeout(refreshTimer)
  document.removeEventListener("visibilitychange", handleVisibilityChange)
  window.removeEventListener(RESERVATION_PENDING_REFRESH_EVENT, handleLocalRefresh)
  pendingStore.clear()
})
</script>

<template>
  <span aria-hidden="true" style="display: none" />
</template>
